import React, { useEffect, useState, useMemo } from 'react';
import {
  AccountDetailsMap,
  AccountPermissionsMap,
  AccountBalance,
  PartnerAccountStatus,
  AccountTransactionsMap,
  AccountPermissions,
  Transaction,
  PartnerAccountsRequest,
} from 'vopay';
import { useLocation } from 'react-router-dom';

import useAccountCreationFailureDialogue from '../../hooks/useDialogues/useAccountCreationFailure.dialogue';
import type { AccountsHandlers, NewAccountData } from './types';
import useFileDownload from '../../hooks/useFiledDownload';

import Routes from './Routes';
import AccountContext, { PartnerAccountContext } from './AccountContext';
import { usePartnerAccountBusinessCase } from '../../providers/PartnerAccountsBusinessCase.provider';
import { useHubSpotData } from '../../providers/HubSpotData.provider';
import { usePartnerAccountMatrix } from '../../providers/PartnerAccountsMatrix.provider';
import { PartnerAccountProvider } from '../../providers/PartnerAccount.provider';
import { usePartnerAccounts } from '../../providers/PartnerAccounts.provider';
import { PendingDataProvider, usePendingData } from '../../providers/PendingData.provider';
import { PartnerAccountRequest } from '../../hooks/useApi/usePostPartnerAccount';
import { usePartnerLogin } from '../../providers/PartnerLogin.provider';
import { useDocument } from '../../providers/Document.provider';
import useStorage from '@/hooks/useStorage';

const AccountsContainer = (): React.JSX.Element => {
  /*

    ▼ setup initial state for partner account container ▼

  */
  const { downloadCsvFile } = useFileDownload();
  const { response: fetchPartnerAccountsResponse, refreshPartnerAccounts } = usePartnerAccounts();
  const { response: businessCaseResponse, postBusinessCase } = usePartnerAccountBusinessCase();
  const { response: hubSpotDataResponse, postHubSpotData } = useHubSpotData();
  const { response: accountMatrixResponse, postMatrix } = usePartnerAccountMatrix();
  const { response: documentUploadResponse, postDocument } = useDocument();

  const [details, setDetails] = useState<AccountDetailsMap>({});
  const [permissions, setPermissions] = useState<AccountPermissionsMap>({});
  const [balances, setBalances] = useState<AccountBalance[]>([]);
  const [shouldFetchData, setShouldFetchData] = useState(false);
  const [uploadDocument, setUploadDocument] = useState(false);
  const { updatePendingData, initPendingData } = usePendingData();

  const location = useLocation();
  const queryParams = useMemo(() => new URLSearchParams(location.search), [location.search]);
  const accountId = useMemo(() => queryParams.get('accountId') || '', [queryParams]);
  const country = useMemo(() => queryParams.get('country') || '', [queryParams]);
  const currency = useMemo(() => queryParams.get('currency') || '', [queryParams]);
  const accountStatus = useMemo(() => (queryParams.get('status') as PartnerAccountStatus) || '', [queryParams]);
  const searchText = useMemo(() => queryParams.get('searchText') || '', [queryParams]);

  /*  ▼ account creation state ▼  */

  const [newAccountData, setNewAccountData] = useState<NewAccountData | null>(null);
  const [hasPendingAccountCreationData, setHasPendingAccountCreationData] = useState(false);

  const [isAccountCreationSuccessful, setAccountCreationSuccess] = useState<boolean | null>(null);

  const [, setOnboardingLink] = useState<string | null>(null);
  const [newAccountId, setNewAccountId] = useState('');
  const { response: partnerLoginResponse } = usePartnerLogin();
  const { isInsideSales } = partnerLoginResponse;
  const {
    partnerTransactions: { getPartnerTransactions, storePartnerTransactions },
  } = useStorage();

  const {
    showDialogue: showAccountsCreationFailureDialogue,
    response: accountCreationResponse,
    postPartnerAccount,
  } = useAccountCreationFailureDialogue();

  /*  ▼ account transactions state ▼  */

  const [transactions, setTransactions] = useState<AccountTransactionsMap>({});

  /*

    ▼ initial call and handling of account data ▼

  */

  useEffect(() => {
    if (fetchPartnerAccountsResponse?.success) {
      if (fetchPartnerAccountsResponse?.CSVFile) {
        downloadCsvFile(fetchPartnerAccountsResponse.CSVFile, 'accounts');
      } else {
        setDetails(fetchPartnerAccountsResponse.accountDetails);
        setBalances(fetchPartnerAccountsResponse.balances);
        setPermissions(fetchPartnerAccountsResponse.permissions);
      }
    } else {
      setBalances([]);
    }
  }, [fetchPartnerAccountsResponse]);

  useEffect(() => {
    if (newAccountData) postPartnerAccount(buildPartnerAccountParams(newAccountData));
  }, [newAccountData]);

  useEffect(() => {
    if (accountCreationResponse?.success) {
      setNewAccountId(accountCreationResponse.accountId);
      setHasPendingAccountCreationData(false);
      setAccountCreationSuccess(true);
      setShouldFetchData(true);
    }

    if (accountCreationResponse?.errorMessage) {
      showAccountsCreationFailureDialogue();
      setHasPendingAccountCreationData(false);
      setAccountCreationSuccess(false);
      setNewAccountData(null);
      setShouldFetchData(false);
    }

    if (accountCreationResponse?.onboardingLink) {
      setOnboardingLink(accountCreationResponse.onboardingLink);
    }
  }, [accountCreationResponse]);

  /*

    ▼ handle account business-case data ▼

  */

  useEffect(() => {
    if (isAccountCreationSuccessful && newAccountData) {
      if (isInsideSales) {
        postHubSpotData({
          DealID: newAccountData.hubspotDealId ?? '',
          CompanyWebsite: newAccountData.companyWebsiteURL ?? '',
          PlatformUsage: newAccountData.companyPlatformUsage ?? '',
          SocialMediaPage: newAccountData.socialMediaPage ?? '',
          CompanyPhone: newAccountData.companyPhone ?? '',
        });
        postMatrix({ ...newAccountData, newAccountId });
      } else {
        postBusinessCase({ ...newAccountData, newAccountId });
      }
    }
  }, [isAccountCreationSuccessful]);

  useEffect(() => {
    if (businessCaseResponse || hubSpotDataResponse || documentUploadResponse || accountMatrixResponse)
      setAccountCreationSuccess(null);
  }, [businessCaseResponse, hubSpotDataResponse, documentUploadResponse, accountMatrixResponse]);

  useEffect(() => {
    if (accountMatrixResponse?.success && isInsideSales && newAccountData) {
      setUploadDocument(true);
      postBusinessCase({ ...newAccountData, newAccountId });
    }
  }, [accountMatrixResponse]);

  useEffect(() => {
    if (uploadDocument && newAccountData && fetchPartnerAccountsResponse?.accountDetails[newAccountId]) {
      postDocument(
        {
          DocumentContent: newAccountData.flowOfFundsDiagram ?? '',
          DocumentName: `Flow of Funds - ${
            newAccountData.businessName || `${newAccountData.firstName ?? ''} ${newAccountData.lastName ?? ''}`
          }`,
          DocumentType: 'flowoffunds',
        },
        fetchPartnerAccountsResponse?.accountDetails[newAccountId]?.token ?? '',
      );
    }
  }, [uploadDocument, fetchPartnerAccountsResponse]);
  useEffect(() => {
    // restore from local storage if we have it
    const partnerTransactionsResponse = getPartnerTransactions();
    if (partnerTransactionsResponse) {
      setTransactions(partnerTransactionsResponse);
    }
  }, []);

  useEffect(() => {
    if (accountId) {
      refreshPartnerAccounts({ VopayAccountID: accountId });
    }
  }, [accountId]);

  useEffect(() => {
    if (shouldFetchData && accountId) {
      refreshPartnerAccounts({ VopayAccountID: accountId });
      setShouldFetchData(false);
    }
  }, [shouldFetchData, accountId]);

  useEffect(() => {
    if (shouldFetchData && newAccountId) {
      refreshPartnerAccounts({ VopayAccountID: newAccountId });
      setShouldFetchData(false);
    }
  }, [shouldFetchData, newAccountId]);

  const handlers: AccountsHandlers = {
    resetAccountsState: () => {
      setHasPendingAccountCreationData(false);
    },

    initializePermissions: (accountPermissions: AccountPermissions) => {
      initPendingData(accountPermissions);
    },

    onPermissionChange: (permissionId: string, permissionValue: string | boolean) => {
      updatePendingData({ [permissionId]: permissionValue });
    },

    onTransactionsFetch: (accountID: string, transactionList: Transaction[]) => {
      setTransactions((prevState: AccountTransactionsMap) => {
        const transactions = {
          ...prevState,
          [accountID]: transactionList,
        };
        storePartnerTransactions(transactions);
        return transactions;
      });
    },

    onAccountCreation: (_newAccountData: NewAccountData) => {
      setNewAccountData(_newAccountData);
    },

    exportCSV: (accountID: string | undefined): void => {
      const requestParams: PartnerAccountsRequest = {
        ExportCSV: true,
        Country: country,
        Currency: currency,
        VopayAccountStatus: accountStatus,
        SearchText: searchText,
      };

      if (accountID && accountID.trim() !== '') {
        requestParams.AccountName = accountID;
      }

      refreshPartnerAccounts(requestParams);
    },
  };

  const partnerAccountContext: PartnerAccountContext = {
    setHasPendingAccountCreationData,
    hasPendingAccountCreationData,
    setShouldFetchData,
    balances,
    details,
    permissions,
    transactions,
  };

  return (
    <AccountContext.Provider value={partnerAccountContext}>
      <Routes handlers={handlers} />
    </AccountContext.Provider>
  );

  function buildPartnerAccountParams(accountData: NewAccountData): PartnerAccountRequest {
    const partnerAccountData: PartnerAccountRequest = {
      accountName: accountData.businessName || accountData.companyBusinessName || '',
      emailAddress: accountData.emailAddress || accountData.businessEmailAddress || '',
      businessName: accountData.companyBusinessName || '',
      fullLegalName: `${accountData.firstName ?? ''} ${accountData.lastName ?? ''}`,
      country: accountData.billingCountry || '',
      packageType: accountData.createBillingPackage && !isInsideSales ? '' : accountData.packageType,
      isClientAccountsEnabled: accountData.isClientAccountsEnabled || false,
      businessType: accountData.businessType || '',
      industry: accountData.industry || '',
      industrySubCategory: accountData.industrySubCategory || '',
      accountingSoftwareIntegrationEnabled: accountData.isIntegrationWithAccountingSoftware || false,
      hubspotDealID: accountData.hubspotDealId,
      hubspotCompanyID: accountData.hubspotCompanyId,
      address: accountData.address || '',
      unit: accountData.unit,
      city: accountData.city || '',
      province: accountData.province || '',
      postalCode: accountData.postalcode || '',
      legalBusinessName: accountData.legalBusinessName || accountData.companyBusinessName || '',
      websiteURL: accountData.websiteURL || accountData.companyWebsiteURL || '',
      businessPhone: accountData.businessPhone || accountData.companyPhone || '',
      legalAgreementNotes: accountData.legalAgreementNotes || '',
      billingPackageData: accountData,
    };
    return partnerAccountData;
  }
};

export default (): React.JSX.Element => (
  <PendingDataProvider>
    <PartnerAccountProvider>
      <AccountsContainer />
    </PartnerAccountProvider>
  </PendingDataProvider>
);
